import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import Typography from '@mui/material/Typography';

import { AppLink } from '@/components/atoms';

import { CRUMB_CURRENT_INK, CRUMB_LINK_INK } from './pageIntroInk';

export type Crumb = { label: string; to?: string };

type Props = {
  items: readonly Crumb[];
  /** Reduced vertical gap in compact catalogue headers. */
  compact?: boolean;
  /** The compact catalogue stage stays dark on phones as well as desktop. */
  solidStage?: boolean;
  /**
   * Inside the opening band, which wears its dark stage from `md` up and sits
   * on the page's own surface below it (pageIntroBand.ts).
   *
   * It used to be `inverse`, a plain boolean meaning "this trail is on a dark
   * surface". That was true of the whole band once and is now only true of
   * part of it, so the name had to stop asserting a colour and start naming
   * the surface — the ink it chooses is a pair of values, one per side of the
   * breakpoint, and `pageIntroInk` holds both.
   *
   * Off by default, which is what the vehicle page and the guide article pass
   * by passing nothing: an ordinary trail on an ordinary page.
   */
  stage?: boolean;
};

/** §60 N. The last crumb is the current page and is not a link. */
export function Breadcrumbs({ items, stage, compact = false, solidStage = false }: Props) {
  const linkInk = stage ? (solidStage ? 'rgba(255,255,255,0.72)' : CRUMB_LINK_INK) : 'text.secondary';
  const currentInk = solidStage ? 'rgba(255,255,255,0.96)' : CRUMB_CURRENT_INK;

  return (
    <MuiBreadcrumbs
      aria-label="Breadcrumb"
      sx={[
        { mb: compact ? { xs: 1.5, md: 2 } : { xs: 3, md: 4 } },
        !!stage && { '& .MuiBreadcrumbs-separator': { color: linkInk } },
      ]}
    >
      {/* `sx` rather than the `color` prop: a responsive object is a valid
          system value but not a valid `LinkProps['color']`, which is typed for
          the palette's named variants. */}
      <AppLink href="/" sx={{ color: linkInk }}>
        Home
      </AppLink>
      {items.map((c, i) =>
        c.to && i < items.length - 1 ? (
          <AppLink key={c.label} href={c.to} sx={{ color: linkInk }}>
            {c.label}
          </AppLink>
        ) : (
          <Typography
            key={c.label}
            sx={{ color: stage ? currentInk : 'text.primary' }}
            aria-current="page"
          >
            {c.label}
          </Typography>
        ),
      )}
    </MuiBreadcrumbs>
  );
}
